import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, Lightning, ChartLine, Rocket, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pro() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const gate = sp.get("gate"); // 'leaderboard' | null — set by ProRoute when redirecting non-Pro users
  const [packages, setPackages] = useState([]);
  const [proStatus, setProStatus] = useState(null);
  const [loading, setLoading] = useState(null); // package_id while submitting
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    api.get("/pro/packages").then((r) => setPackages(r.data.packages || []));
    if (user) api.get("/pro/me").then((r) => setProStatus(r.data));
  }, [user]);

  const upgrade = async (pkg) => {
    setLoading(pkg.package_id);
    try {
      // New recurring-subscription flow (Stripe SDK).
      // Maps the legacy `pro_monthly` / `pro_yearly` package_ids to the
      // subscribe endpoint's `plan` param (monthly | annual).
      const plan = pkg.package_id === "pro_yearly" ? "annual" : "monthly";
      const { data } = await api.post("/pro/subscribe", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const status = e.response?.status;
      // 503 → subscription billing not configured (preview/sandbox) → fall back
      // to the legacy one-shot DKK package checkout so the demo still works.
      if (status === 503) {
        try {
          const { data } = await api.post("/payments/checkout/session", {
            package_id: pkg.package_id,
            origin_url: window.location.origin,
          });
          window.location.href = data.url;
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Couldn't start checkout");
        }
      } else {
        toast.error(e.response?.data?.detail || "Couldn't start checkout");
      }
      setLoading(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm(lang === "da"
      ? "Annullér Pro Crew? Du beholder Pro indtil udløb, men der opkræves ingen yderligere beløb."
      : "Cancel Pro Crew? You keep Pro until it expires, but you won't be charged again.")) return;
    setCancelling(true);
    try {
      await api.post("/pro/cancel");
      toast.success(lang === "da" ? "Pro annulleret. Aktiv indtil udløb." : "Pro cancelled. Active until expiry.");
      const { data } = await api.get("/pro/me");
      setProStatus(data);
    } catch (e) {
      toast.error("Couldn't cancel");
    } finally { setCancelling(false); }
  };

  const isPro = !!proStatus?.is_pro;
  const expDate = proStatus?.pro_expires_at ? new Date(proStatus.pro_expires_at) : null;
  const trialEligible = proStatus && !proStatus.trial_used && !isPro;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Crown weight="fill" size={28} color="#FFCC00" />
          <div className="label-overline" style={{ color: "#FFCC00" }}>{lang === "da" ? "Pro Crew" : "Pro Crew"}</div>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight">
          {lang === "da" ? "BLIV PRO. LED HOLDET." : "GO PRO. LEAD THE CREW."}
        </h1>
        <p className="text-zinc-300 mt-4 max-w-2xl text-base">
          {lang === "da"
            ? "Lås op for større grupper, boostet synlighed i Udforsk og et privat analytics-dashboard. Pro Crew gør dig til en bedre vært."
            : "Unlock larger groups, boosted visibility on Explore, and a private analytics dashboard. Pro Crew makes you a sharper host."}
        </p>

        {isPro && (
          <section
            className="mt-8 border border-[#FFCC00] bg-[#0A0A0A] p-6"
            data-testid="pro-active-card"
          >
            {/* PRO AKTIV pill */}
            <div className="inline-flex items-center gap-2 bg-[#FFCC00] text-black px-3 py-1.5 font-bold text-xs uppercase tracking-widest">
              <Crown weight="fill" size={14} />
              {lang === "da" ? "Pro aktiv" : "Pro active"}
            </div>

            {expDate && (
              <div className="mt-3 text-xs text-zinc-400">
                {lang === "da" ? "Aktiv til" : "Active until"}{" "}
                <span className="font-mono-tag text-[#FFCC00]">
                  {expDate.toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
                {proStatus?.cancel_at_period_end && (
                  <span className="ml-2 text-[#FF9F0A]">
                    · {lang === "da" ? "Annulleres ved periodens slutning" : "Cancels at period end"}
                  </span>
                )}
              </div>
            )}

            {/* PRO-FUNKTIONER label */}
            <div className="label-overline mt-5 text-zinc-500">
              {lang === "da" ? "Pro-funktioner" : "Pro features"}
            </div>

            {/* Quick-access feature cards */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Link
                to="/leaderboard"
                data-testid="pro-active-leaderboard"
                className="border border-[#FFCC00]/60 bg-[#141414] p-4 hover:bg-[#1a1a1a] transition-colors block"
              >
                <div className="w-10 h-10 rounded-full border-2 border-[#FFCC00] flex items-center justify-center mb-3">
                  <Trophy weight="fill" size={18} color="#FFCC00" />
                </div>
                <div className="font-bold text-sm">{lang === "da" ? "Rangliste" : "Leaderboard"}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-snug">
                  {lang === "da" ? "Top værter og mest aktive sessioner" : "Top hosts and most active sessions"}
                </div>
              </Link>
              <Link
                to="/analytics"
                data-testid="pro-active-weekly"
                className="border border-[#FFCC00]/60 bg-[#141414] p-4 hover:bg-[#1a1a1a] transition-colors block"
              >
                <div className="w-10 h-10 rounded-full border-2 border-[#FFCC00] flex items-center justify-center mb-3">
                  <ChartLine weight="fill" size={18} color="#FFCC00" />
                </div>
                <div className="font-bold text-sm">{lang === "da" ? "Ugentlig rapport" : "Weekly report"}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-snug">
                  {lang === "da" ? "Din uge i tal" : "Your week in numbers"}
                </div>
              </Link>
            </div>

            {/* STATISTIK button */}
            <Link
              to="/analytics"
              data-testid="pro-active-stats"
              className="mt-3 block text-center border border-[#262626] hover:border-white/40 py-3 font-bold uppercase tracking-widest text-xs"
            >
              {lang === "da" ? "Statistik" : "Statistics"}
            </Link>

            {/* ANNULLER ABONNEMENT button */}
            {!proStatus?.cancel_at_period_end && (
              <button
                type="button"
                onClick={cancel}
                disabled={cancelling}
                data-testid="pro-active-cancel"
                className="mt-3 w-full border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50 py-3 font-bold uppercase tracking-widest text-xs transition-colors"
              >
                {cancelling
                  ? (lang === "da" ? "Annullerer…" : "Cancelling…")
                  : (lang === "da" ? "Annuller abonnement" : "Cancel subscription")}
              </button>
            )}

            <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
              {lang === "da"
                ? "Annullering stopper fremtidige fornyelser. Din Pro-adgang forbliver aktiv indtil periodens slutning."
                : "Cancelling stops future renewals. Your Pro access stays active until the end of the current period."}
            </p>
          </section>
        )}

        {gate === "leaderboard" && !isPro && (
          <div
            className="mt-6 inline-flex items-center gap-3 border border-[#FF6B6B]/40 bg-[#FF6B6B]/10 px-4 py-3 flex-wrap"
            data-testid="pro-gate-leaderboard-banner"
          >
            <Trophy weight="fill" size={18} color="#FF6B6B" />
            <div className="text-sm">
              {lang === "da"
                ? "Ranglisten er en Pro-funktion. Opgradér nedenfor for at se hvor du rangerer."
                : "The leaderboard is a Pro feature. Upgrade below to see where you rank."}
            </div>
          </div>
        )}

        {trialEligible && (
          <div
            className="mt-6 inline-flex items-center gap-3 border border-[#34C759]/40 bg-[#34C759]/10 px-4 py-3 flex-wrap"
            data-testid="pro-trial-banner"
          >
            <Lightning weight="fill" size={18} color="#34C759" />
            <div className="text-sm">
              {lang === "da"
                ? "Gratis i 14 dage. Ingen opkrævning før prøveperioden slutter — annullér når som helst."
                : "Free for 14 days. No charge until the trial ends — cancel anytime."}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          <Perk
            color="#34C759"
            Icon={Rocket}
            title={lang === "da" ? "Større hold" : "Larger crews"}
            text={lang === "da"
              ? "Op til 50 deltagere pr. session i stedet for 10."
              : "Up to 50 participants per session instead of 10."}
          />
          <Perk
            color="#007AFF"
            Icon={Lightning}
            title={lang === "da" ? "Boostet på Udforsk" : "Boosted on Explore"}
            text={lang === "da"
              ? "Dine sessioner får forrang i feedet — flere medlemmer, hurtigere."
              : "Your sessions jump to the top of the feed — more members, faster."}
          />
          <Perk
            color="#FFCC00"
            Icon={ChartLine}
            title={lang === "da" ? "Privat analytics" : "Private analytics"}
            text={lang === "da"
              ? "Følg deltagelse, ratings og fill rate på dit dashboard."
              : "Track participation, ratings, and fill rate on your dashboard."}
          />
          <Perk
            color="#FF6B6B"
            Icon={Trophy}
            title={lang === "da" ? "Rangliste" : "Leaderboard"}
            text={lang === "da"
              ? "Se hvor du rangerer pr. uge, måned eller all-time — pr. sport eller samlet."
              : "See where you rank weekly, monthly, or all-time — by sport or overall."}
            testid="perk-leaderboard"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-10">
          {packages.map((pkg) => (
            <div key={pkg.package_id} data-testid={`pro-package-${pkg.package_id}`} className="tf-card p-6 flex flex-col">
              <div className="flex items-baseline justify-between">
                <div className="font-display text-3xl tracking-tight">{pkg.label.toUpperCase()}</div>
                {pkg.package_id === "pro_yearly" && (
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-[#34C759]/20 text-[#34C759] border border-[#34C759]/40 uppercase tracking-widest">
                    {lang === "da" ? "Spar 17%" : "Save 17%"}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-5xl">{pkg.amount}</span>
                <span className="label-overline">{pkg.currency.toUpperCase()}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                {pkg.package_id === "pro_monthly"
                  ? (lang === "da" ? "for 30 dage" : "for 30 days")
                  : (lang === "da" ? "for 365 dage" : "for 365 days")}
              </div>
              <ul className="mt-6 space-y-2 text-sm">
                <Bullet>{lang === "da" ? "Op til 50 deltagere pr. session" : "Up to 50 participants per session"}</Bullet>
                <Bullet>{lang === "da" ? "Boostet placering på Udforsk" : "Boosted placement on Explore"}</Bullet>
                <Bullet>{lang === "da" ? "Privat analytics-dashboard" : "Private analytics dashboard"}</Bullet>
                <Bullet>{lang === "da" ? "Pro-mærke ved dit navn" : "Pro badge on your profile"}</Bullet>
              </ul>
              <button
                data-testid={`upgrade-${pkg.package_id}`}
                disabled={loading === pkg.package_id}
                onClick={() => upgrade(pkg)}
                className="kinetic-btn mt-6 w-full bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-50 text-black px-4 py-3 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2"
              >
                <Crown weight="fill" size={16} />
                {loading === pkg.package_id
                  ? (lang === "da" ? "Omdirigerer…" : "Redirecting…")
                  : (isPro
                    ? (lang === "da" ? "Forlæng abonnement" : "Extend subscription")
                    : trialEligible
                      ? (lang === "da" ? "Start 14 dages gratis prøve" : "Start 14-day free trial")
                      : (lang === "da" ? "Bliv Pro" : "Upgrade to Pro"))}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-10 text-xs text-zinc-500">
          {lang === "da"
            ? "Sikker betaling via Stripe. Beløbet trækkes når du gennemfører betalingen. Du kan til enhver tid annullere — Pro forbliver aktiv til udløb."
            : "Secure payment via Stripe. Charged once on checkout. Cancel anytime — Pro stays active until expiry."}
        </div>
      </main>
    </div>
  );
}

function Perk({ Icon, color, title, text, testid }) {
  return (
    <div className="tf-card p-5" data-testid={testid}>
      <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ background: `${color}1F`, border: `1px solid ${color}` }}>
        <Icon weight="duotone" size={22} color={color} />
      </div>
      <div className="font-display text-xl tracking-tight">{title}</div>
      <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{text}</div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check weight="bold" size={14} className="mt-0.5 text-[#34C759] shrink-0" />
      <span>{children}</span>
    </li>
  );
}
